import React, { useState, useEffect } from "react";
import { getFunctions, httpsCallable } from "firebase/functions";
import { getAllUsers, approveUser, unapproveUser } from "../../firebase";
import { UserProfile } from "../../types";
import SearchIcon from "@mui/icons-material/Search";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import EmailIcon from "@mui/icons-material/Email";

// Type for a user with approval status
type UserWithApproval = UserProfile & { isApproved: boolean };

const UserManagement: React.FC = () => {
  const [users, setUsers] = useState<UserWithApproval[]>([]);
  const [filteredUsers, setFilteredUsers] = useState<UserWithApproval[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [page, setPage] = useState<number>(0);
  const [rowsPerPage, setRowsPerPage] = useState<number>(10);
  const [processingUser, setProcessingUser] = useState<string | null>(null);
  
  // New state for the approval confirmation dialog
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [userToApprove, setUserToApprove] = useState<UserWithApproval | null>(
    null
  );
  const [notifyUser, setNotifyUser] = useState(true);

  // Email functionality
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [emailData, setEmailData] = useState({
    title: "",
    message: "",
  });
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const [emailSuccess, setEmailSuccess] = useState<string | null>(null);

  // Load all users from Firestore
  useEffect(() => {
    const loadUsers = async () => {
      try {
        setLoading(true);
        // getAllUsers now fetches data from both the users and approved collections
        const allUsers = await getAllUsers();
        setUsers(allUsers);
        setFilteredUsers(allUsers);
      } catch (err: any) {
        console.error("Error loading users:", err);
        setError(err.message || "Failed to load users");
      } finally {
        setLoading(false);
      }
    };

    loadUsers();
  }, []);

  // Filter users based on search term
  useEffect(() => {
    if (searchTerm.trim() === "") {
      const sortedUsers = [...users].sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : null;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : null;
        
        // If both dates are valid, sort by most recent first
        if (dateA && dateB) {
          return dateB - dateA;
        }
        
        // If only one date is valid, prioritize the valid date
        if (dateA && !dateB) {
          return -1; // a comes first
        }
        if (!dateA && dateB) {
          return 1; // b comes first
        }
        
        // If both dates are invalid, maintain original order
        return 0;
      });
      setFilteredUsers(sortedUsers);
    } else {
      const lowercasedFilter = searchTerm.toLowerCase();
      const filtered = users.filter((user) => {
        return (
          (user.displayName &&
            user.displayName.toLowerCase().includes(lowercasedFilter)) ||
          (user.email && user.email.toLowerCase().includes(lowercasedFilter))
        );
      });
      
      // Sort filtered results by registration date
      const sortedFilteredUsers = filtered.sort((a, b) => {
        const dateA = a.createdAt ? new Date(a.createdAt).getTime() : null;
        const dateB = b.createdAt ? new Date(b.createdAt).getTime() : null;
        
        // If both dates are valid, sort by most recent first
        if (dateA && dateB) {
          return dateB - dateA;
        }
        
        // If only one date is valid, prioritize the valid date
        if (dateA && !dateB) {
          return -1; // a comes first
        }
        if (!dateA && dateB) {
          return 1; // b comes first
        }
        
        // If both dates are invalid, maintain original order
        return 0;
      });
      
      setFilteredUsers(sortedFilteredUsers);
      setPage(0); // Reset to first page when filtering
    }
  }, [searchTerm, users]);

  const handleApproveUser = (user: UserWithApproval) => {
    setUserToApprove(user);
    setNotifyUser(true);
    setIsConfirmDialogOpen(true);
  };

  const executeApproveUser = async () => {
    if (!userToApprove) return;

    const userToApproveId = userToApprove.uid;
    const userEmail = userToApprove.email;
    
    setProcessingUser(userToApproveId);
    setIsConfirmDialogOpen(false);

    try {
      // Step 1: Approve the user in Firestore
      await approveUser(userToApproveId);

      // Step 2: Send a notification email to the user
      if (notifyUser && userEmail) {
        const functions = getFunctions();
        const sendGeneralEmailFunction = httpsCallable(
          functions,
          "sendGeneralEmailFunction"
        );

        const firstName = userToApprove.displayName?.split(" ")[0] || "there";

        const emailPayload = {
          emailAddresses: [userEmail],
          title: "You're Approved! Welcome to Wattlenest",
          message: `Hi ${firstName},\n\nGreat news! Your account on the Wattlenest portal has been approved. You now have full access to all resources and features.\n\n https://wattlenest.com.au/login  \n\nWe're excited to have you as part of the community.`,
        };

        await sendGeneralEmailFunction(emailPayload);
      }

      // Step 3: Update local state
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.uid === userToApproveId ? { ...user, isApproved: true } : user
        )
      );
    } catch (err: any) {
      console.error("Error approving user:", err);
      setError(`Failed to approve user: ${err.message}`);
    } finally {
      setProcessingUser(null);
      setUserToApprove(null);
    }
  };

  const handleUnapproveUser = async (userId: string) => {
    try {
      setProcessingUser(userId);
      // unapproveUser now removes the user from the approved collection
      await unapproveUser(userId);

      // Update local state
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.uid === userId ? { ...user, isApproved: false } : user
        )
      );
    } catch (err: any) {
      console.error("Error unapproving user:", err);
      setError(`Failed to unapprove user: ${err.message}`);
    } finally {
      setProcessingUser(null);
    }
  };

  const handleChangePage = (newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Email functionality
  const handleOpenEmailDialog = () => {
    setEmailData({
      title: "",
      message: ``,
    });
    setEmailError(null);
    setEmailSuccess(null);
    setEmailDialogOpen(true);
  };

  const handleCloseEmailDialog = () => {
    setEmailDialogOpen(false);
  };

  const handleSendEmail = async () => {
    if (!emailData.title.trim() || !emailData.message.trim()) {
      setEmailError("Please provide both a title and message.");
      return;
    }

    // Get email addresses of approved users only
    const approvedUserEmails = users
      .filter(user => user.isApproved && user.email)
      .map(user => user.email)
      .filter(email => email && email.trim() !== "");

    if (approvedUserEmails.length === 0) {
      setEmailError("No approved users found with valid email addresses.");
      return;
    }

    setEmailLoading(true);
    setEmailError(null);
    setEmailSuccess(null);

    try {
      const functions = getFunctions();
      const sendGeneralEmailFunction = httpsCallable(functions, 'sendGeneralEmailFunction');
      
      const result = await sendGeneralEmailFunction({
        title: emailData.title,
        message: emailData.message,
        emailAddresses: approvedUserEmails
      });

      setEmailSuccess(`Email sent successfully to ${approvedUserEmails.length} approved athletes!`);
      
      // Close dialog after a short delay
      setTimeout(() => {
        setEmailDialogOpen(false);
        setEmailSuccess(null);
      }, 2000);

    } catch (error: any) {
      console.error("Error sending email:", error);
      setEmailError(error.message || "Failed to send email. Please try again.");
    } finally {
      setEmailLoading(false);
    }
  };

  // Calculate pagination
  const startIndex = page * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedUsers = filteredUsers.slice(startIndex, endIndex);
  const totalPages = Math.ceil(filteredUsers.length / rowsPerPage);

  return (
    <div className="bg-white rounded-lg">
      {/* Search and filter section */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex justify-between items-center mb-4">
          <button
            onClick={handleOpenEmailDialog}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-gold hover:bg-gold/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold"
          >
            <EmailIcon className="h-4 w-4 mr-2" />
            Email Approved Athletes
          </button>
        </div>
        <div className="relative">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <SearchIcon className="h-5 w-5 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Search users by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10 pr-4 py-2 border border-gray-300 rounded-md w-full focus:outline-none focus:ring-2 focus:ring-gold focus:border-transparent"
          />
        </div>
      </div>

      {/* Error message */}
      {error && (
        <div className="p-4 bg-red-50 border-l-4 border-red-500 text-red-700">
          <p>{error}</p>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                User
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Email
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Registration Date
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Status
              </th>
              <th
                scope="col"
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center">
                  <div className="flex justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gold"></div>
                  </div>
                </td>
              </tr>
            ) : paginatedUsers.length === 0 ? (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-gray-500">
                  No users found
                </td>
              </tr>
            ) : (
              paginatedUsers.map((user) => (
                <tr key={user.uid} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="flex-shrink-0 h-10 w-10">
                        {user.photoURL ? (
                          <img
                            className="h-10 w-10 rounded-full"
                            src={user.photoURL}
                            alt={user.displayName || "User"}
                          />
                        ) : (
                          <div className="h-10 w-10 rounded-full bg-gray-200 flex items-center justify-center text-gray-500">
                            {user.displayName
                              ? user.displayName.charAt(0).toUpperCase()
                              : "U"}
                          </div>
                        )}
                      </div>
                      <div className="ml-4">
                        <div className="text-sm font-medium text-gray-900">
                          {user.displayName || "No name"}
                        </div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{user.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-500">
                      {user.createdAt
                        ? new Date(user.createdAt).toLocaleDateString()
                        : "Unknown"}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        user.isApproved
                          ? "bg-green-100 text-green-800"
                          : "bg-gray-100 text-gray-800"
                      }`}
                    >
                      {user.isApproved ? (
                        <>
                          <CheckCircleIcon className="h-4 w-4 mr-1" />
                          Approved
                        </>
                      ) : (
                        <>
                          <CancelIcon className="h-4 w-4 mr-1" />
                          Not Approved
                        </>
                      )}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {processingUser === user.uid ? (
                      <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-gold"></div>
                    ) : user.isApproved ? (
                      <button
                        className="text-yellow-600 hover:text-yellow-900 bg-yellow-100 hover:bg-yellow-200 px-3 py-1 rounded-md text-sm font-medium transition-colors"
                        onClick={() => handleUnapproveUser(user.uid)}
                      >
                        Remove Approval
                      </button>
                    ) : (
                      <button
                        className="text-green-600 hover:text-green-900 bg-green-100 hover:bg-green-200 px-3 py-1 rounded-md text-sm font-medium transition-colors inline-flex items-center"
                        onClick={() => handleApproveUser(user)}
                      >
                        <HowToRegIcon className="h-4 w-4 mr-1" />
                        Approve
                      </button>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
        <div className="flex-1 flex justify-between sm:hidden">
          <button
            onClick={() => handleChangePage(page - 1)}
            disabled={page === 0}
            className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
              page === 0
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            Previous
          </button>
          <button
            onClick={() => handleChangePage(page + 1)}
            disabled={page >= totalPages - 1}
            className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${
              page >= totalPages - 1
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "bg-white text-gray-700 hover:bg-gray-50"
            }`}
          >
            Next
          </button>
        </div>
        <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
          <div>
            <p className="text-sm text-gray-700">
              Showing <span className="font-medium">{startIndex + 1}</span> to{" "}
              <span className="font-medium">
                {Math.min(endIndex, filteredUsers.length)}
              </span>{" "}
              of <span className="font-medium">{filteredUsers.length}</span>{" "}
              results
            </p>
          </div>
          <div>
            <div className="flex items-center">
              <select
                id="rows-per-page"
                name="rows-per-page"
                className="mr-4 block w-full pl-3 pr-10 py-2 text-base border-gray-300 focus:outline-none focus:ring-gold focus:border-gold sm:text-sm rounded-md"
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
              >
                {[5, 10, 25, 50].map((value) => (
                  <option key={value} value={value}>
                    {value} per page
                  </option>
                ))}
              </select>

              <nav
                className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px"
                aria-label="Pagination"
              >
                <button
                  onClick={() => handleChangePage(0)}
                  disabled={page === 0}
                  className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${
                    page === 0
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  <span className="sr-only">First</span>
                  <svg
                    className="h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => handleChangePage(page - 1)}
                  disabled={page === 0}
                  className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                    page === 0
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  <span className="sr-only">Previous</span>
                  <svg
                    className="h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>

                {/* Page numbers */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  // Show pages around current page
                  let pageNum = 0;
                  if (totalPages <= 5) {
                    pageNum = i;
                  } else if (page < 3) {
                    pageNum = i;
                  } else if (page > totalPages - 3) {
                    pageNum = totalPages - 5 + i;
                  } else {
                    pageNum = page - 2 + i;
                  }

                  return (
                    <button
                      key={pageNum}
                      onClick={() => handleChangePage(pageNum)}
                      className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium ${
                        page === pageNum
                          ? "z-10 bg-gold text-white border-gold"
                          : "bg-white text-gray-500 hover:bg-gray-50"
                      }`}
                    >
                      {pageNum + 1}
                    </button>
                  );
                })}

                <button
                  onClick={() => handleChangePage(page + 1)}
                  disabled={page >= totalPages - 1}
                  className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${
                    page >= totalPages - 1
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  <span className="sr-only">Next</span>
                  <svg
                    className="h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
                <button
                  onClick={() => handleChangePage(totalPages - 1)}
                  disabled={page >= totalPages - 1}
                  className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${
                    page >= totalPages - 1
                      ? "text-gray-300 cursor-not-allowed"
                      : "text-gray-500 hover:bg-gray-50"
                  }`}
                >
                  <span className="sr-only">Last</span>
                  <svg
                    className="h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 20 20"
                    fill="currentColor"
                    aria-hidden="true"
                  >
                    <path
                      fillRule="evenodd"
                      d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z"
                      clipRule="evenodd"
                    />
                  </svg>
                </button>
              </nav>
            </div>
          </div>
        </div>
      </div>

      {/* Approval Confirmation Dialog */}
      {isConfirmDialogOpen && userToApprove && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            ></div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-green-100 sm:mx-0 sm:h-10 sm:w-10">
                    <HowToRegIcon
                      className="h-6 w-6 text-green-600"
                      aria-hidden="true"
                    />
                  </div>
                  <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
                    <h3
                      className="text-lg leading-6 font-medium text-gray-900"
                      id="modal-title"
                    >
                      Approve User
                    </h3>
                    <div className="mt-2">
                      <p className="text-sm text-gray-500">
                        Are you sure you want to approve{" "}
                        <span className="font-bold">
                          {userToApprove.displayName || "this user"}
                        </span>
                        ?
                      </p>
                      <p className="text-sm text-gray-500 mt-2">
                        This user will be notified of their approval by email.
                      </p>
                    </div>
                    <div className="mt-4">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          className="form-checkbox h-5 w-5 text-gold rounded focus:ring-gold"
                          checked={notifyUser}
                          onChange={(e) => setNotifyUser(e.target.checked)}
                        />
                        <span className="ml-2 text-sm text-gray-700">
                          Notify user by email
                        </span>
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gold text-base font-medium text-white hover:bg-gold-dark focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={executeApproveUser}
                >
                  Yes, approve
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => setIsConfirmDialogOpen(false)}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Email Dialog */}
      {emailDialogOpen && (
        <div
          className="fixed inset-0 z-50 overflow-y-auto"
          aria-labelledby="modal-title"
          role="dialog"
          aria-modal="true"
        >
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div
              className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
              aria-hidden="true"
            ></div>
            <span
              className="hidden sm:inline-block sm:align-middle sm:h-screen"
              aria-hidden="true"
            >
              &#8203;
            </span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-4xl sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6">
                <div className="flex justify-between items-start">
                  <h3 className="text-lg leading-6 font-medium text-gray-900">
                    Email Approved Athletes
                  </h3>
                  <button
                    type="button"
                    onClick={handleCloseEmailDialog}
                    className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none"
                  >
                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>

                <div className="mt-4">
                  {emailLoading ? (
                    <div className="flex justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-gold"></div>
                    </div>
                  ) : (
                    <>
                      {emailError && (
                        <div className="mb-4 p-4 bg-red-50 text-red-700 rounded-md">
                          {emailError}
                        </div>
                      )}
                      {emailSuccess && (
                        <div className="mb-4 p-4 bg-green-50 text-green-700 rounded-md">
                          {emailSuccess}
                        </div>
                      )}
                      <div>
                        <label
                          htmlFor="email-title"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Email Title
                        </label>
                        <input
                          type="text"
                          id="email-title"
                          value={emailData.title}
                          onChange={(e) =>
                            setEmailData({
                              ...emailData,
                              title: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md shadow-sm border-gray-300 focus:ring-gold focus:border-gold text-base py-3 px-4"
                          placeholder="Enter email title..."
                        />
                      </div>
                      <div className="mt-6">
                        <label
                          htmlFor="email-message"
                          className="block text-sm font-medium text-gray-700 mb-2"
                        >
                          Email Message
                        </label>
                        <textarea
                          id="email-message"
                          value={emailData.message}
                          onChange={(e) =>
                            setEmailData({
                              ...emailData,
                              message: e.target.value,
                            })
                          }
                          className="mt-1 block w-full rounded-md shadow-sm border-gray-300 focus:ring-gold focus:border-gold text-base py-3 px-4 min-h-[300px] resize-y"
                          placeholder="Enter email message here..."
                        />
                        <p className="mt-2 text-sm text-gray-500">
                          You can use line breaks to format your message. The email will be sent to all approved athletes.
                        </p>
                      </div>
                      <div className="mt-6">
                        <button
                          type="button"
                          onClick={handleSendEmail}
                          className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-gold text-base font-medium text-white hover:bg-gold/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gold sm:w-auto sm:text-sm"
                        >
                          Send Email
                        </button>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
